/**
 * SpotlightOverlay — full-page dim with a cutout around the active anchor
 * (and, for adjacent placement, both anchor + coachmark).
 *
 * Implementation: instead of a clip-path mask (which has cross-browser
 * quirks on Safari for the spotlight pulse), we use the design's
 * `box-shadow: 0 0 0 9999px rgba(0,0,0,0.55)` cutout pattern on the
 * positioned spotlight element. The spotlight itself is a transparent
 * rectangle sized + positioned to the anchor's bounding rect. The rest of
 * the page is `aria-hidden="true"` while the tour is active.
 */

import React, { useEffect, useLayoutEffect, useState } from 'react';

const PADDING = 8;

function getRect(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

export default function SpotlightOverlay({ anchorEl, adjacent, interactive = false, children }) {
  const [rect, setRect] = useState(() => getRect(anchorEl));

  // Recompute on resize, scroll, and when the anchor changes. Layout
  // effect runs synchronously so the cutout doesn't flash mis-positioned
  // on first paint.
  useLayoutEffect(() => {
    const recompute = () => setRect(getRect(anchorEl));
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [anchorEl]);

  // While the tour is active, hide the rest of the page from screen readers.
  // The anchor stays interactive (clicking it counts as "advance") because
  // it's not under the overlay.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.querySelector('main') || document.body;
    if (!root) return undefined;
    const prev = root.getAttribute('aria-hidden');
    root.setAttribute('aria-hidden', 'true');
    return () => {
      if (prev === null) root.removeAttribute('aria-hidden');
      else root.setAttribute('aria-hidden', prev);
    };
  }, []);

  if (!rect) return null;

  return (
    <div
      className={`tour-overlay${interactive ? '' : ' tour-overlay--blocking'}`}
      data-testid="tour-overlay"
      data-adjacent={adjacent ? 'true' : 'false'}
    >
      <div
        className={`tour-spotlight${adjacent ? ' tour-spotlight--adjacent' : ''}`}
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
      />
      {children}
    </div>
  );
}
